import { useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { privateAdminRoutes } from '~/utils/routes/admin';
import { appVersion } from '~/utils/constants/appVersion';

const Sidebar = ({
  isMobileMenuOpen,
  onMobileMenuClose,
}: {
  isMobileMenuOpen: boolean;
  onMobileMenuClose: () => void;
}) => {
  const { t } = useTranslation();
  const mobileDrawerRef = useRef<HTMLElement>(null);
  const wasMobileMenuOpen = useRef(false);
  const renderLinks = (mobile = false) =>
    privateAdminRoutes.map((route) => (
      <NavLink
        key={route.path}
        to={route.path}
        onClick={onMobileMenuClose}
        tabIndex={mobile && !isMobileMenuOpen ? -1 : undefined}
        className={({ isActive }) =>
          `rounded-md px-3 py-2 text-sm transition ${
            isActive
              ? 'bg-accent font-semibold text-accent-foreground'
              : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          }`
        }
      >
        {route.name ? t(route.name) : ''}
      </NavLink>
    ));

  useEffect(() => {
    if (!isMobileMenuOpen) {
      if (wasMobileMenuOpen.current) {
        document.getElementById('mobile-menu-trigger')?.focus();
      }
      wasMobileMenuOpen.current = false;
      return;
    }

    wasMobileMenuOpen.current = true;
    const drawer = mobileDrawerRef.current;
    const focusableElements = drawer?.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    focusableElements?.[0]?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onMobileMenuClose();
        return;
      }

      if (event.key !== 'Tab' || !focusableElements?.length) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMobileMenuOpen, onMobileMenuClose]);

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-black/40 transition-opacity duration-300 md:hidden ${
          isMobileMenuOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        aria-hidden='true'
      >
        <button
          type='button'
          className='absolute inset-0 h-full w-full cursor-default'
          onClick={onMobileMenuClose}
          tabIndex={-1}
          aria-label={t('nav.closeMenu')}
        />
      </div>
      <aside
        ref={mobileDrawerRef}
        id='mobile-navigation'
        role='dialog'
        aria-modal='true'
        aria-label={t('nav.menu')}
        aria-hidden={!isMobileMenuOpen}
        className={`fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] flex-col border-r border-border bg-card p-3 shadow-xl transition-transform duration-300 ease-out md:hidden ${
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className='mb-3 flex items-center justify-between border-b border-border pb-3'>
          <span className='px-3 text-sm font-semibold text-foreground'>{t('nav.menu')}</span>
          <button
            type='button'
            onClick={onMobileMenuClose}
            tabIndex={isMobileMenuOpen ? undefined : -1}
            className='inline-flex h-9 w-9 items-center justify-center rounded-md text-foreground transition hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring'
            aria-label={t('nav.closeMenu')}
          >
            <svg
              width='20'
              height='20'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
              aria-hidden='true'
            >
              <path d='M18 6 6 18M6 6l12 12' />
            </svg>
          </button>
        </div>
        <nav className='flex flex-1 flex-col gap-1 overflow-y-auto'>{renderLinks(true)}</nav>
        <div className='pt-4 text-center text-xs text-muted-foreground'>{appVersion}</div>
      </aside>
      <nav
        id='sidebar'
        className='hidden w-56 shrink-0 flex-col gap-1 overflow-y-auto border-r border-border bg-card p-3 md:flex'
      >
        {renderLinks()}
        <div className='mt-auto pt-4 text-center text-xs text-muted-foreground'>{appVersion}</div>
      </nav>
    </>
  );
};

export default Sidebar;
